import { useState, useEffect } from "react";
import { Spinner, Alert, Form, FormLabel } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SubTitleSection from "src/components/SubTitleSection/SubTitleSection";
import { StyledForm } from "../../GlobalStyle";
import Button from "../Button/Button";
import styled from "styled-components";
import { useToast } from "src/context/toast/toast.provider";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import { useAccount } from "src/context/account/account.provider";
import { captureFormError, addBreadcrumb } from "src/utils/sentry";

/**
 * Instructions for Using DynamicGoogleFormSubmitter Component:
 *
 * 1. Add the `DynamicGoogleFormSubmitter` component to your React project.
 * 2. Pass the following props to the component:
 *    - `formId` (string): The Google Apps Script deployment ID for the form submission endpoint.
 *    - `promoSourceRef` (string, optional): A reference string to track the promo source.
 *    - `formFields` (array): An array of objects defining the form fields. Each object should have:
 *        - `id` (string): Unique identifier for the field.
 *        - `label` (string): Translation key for the field label.
 *        - `type` (string, optional): Input type (e.g., "text", "email"). Defaults to "text".
 *        - `placeholder` (string, optional): Translation key for the placeholder text.
 *        - `required` (boolean, optional): Whether the field is required.
 *    - `formTextKey` (string, optional): Translation namespace for custom form text.
 *
 * 3. Ensure the `formId` corresponds to a Google Apps Script deployment that handles form submissions.
 *
 * Example Google Apps Script:
 *
 * ```javascript
 * function doPost(e) {
 *   const sheet = SpreadsheetApp.getActiveSpreadsheet().getActiveSheet();
 *   const params = e.parameter;
 *
 *   const row = [
 *     new Date(),
 *     params.Language?.trim() || "",
 *     params.FirstName?.trim() || "",
 *     params.LastName?.trim() || "",
 *     params.Email?.trim() || "",
 *     params.PhoneNumber?.trim() || "",
 *     params.PlayerID?.trim() || "",
 *     params.FatherNominee?.trim() || ""
 *   ];
 *
 *   sheet.appendRow(row);
 *
 *   return ContentService.createTextOutput("Success");
 * }
 * ```
 *
 * 4. Attach the above Apps Script to a Google Sheet to store form submissions.
 * 5. Deploy the Apps Script as a web app and use the deployment ID as the `formId` prop.
 * 6. Customize the translations for form text in your i18n configuration, if needed.
 */

const DynamicGoogleFormSubmitter = ({
  formId,
  promoSourceRef,
  formFields = [],
  formTextKey,
  titles = true,
  sheetName,
  // Optional: externally controlled values that should be merged into the form
  // e.g., { Prize: 25, Email: "user@example.com" }
  values,
  disabled = false,
  onFieldChange,
}) => {
  const { t, i18n } = useTranslation();
  const { showToastFn } = useToast();
  const { orderIdFn, orderId } = useAccount();
  const [promoSource] = useState(promoSourceRef);
  const [fieldValues, setFieldValues] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  // Helper function to get text from custom form key or dynamicGoogleForm namespace or default
  const getFormText = (key) => {

    // 1. Try to get from dynamicGoogleForm namespace in the same locale file
    if (formTextKey) {
      const dynamicText = t(`${formTextKey}.dynamicGoogleForm.${key}`, { defaultValue: null });
      if (dynamicText !== null) return dynamicText;
    }

    // 2. Fall back to default form texts from dynamicGoogleFormDefault.json
    return t(`dynamicGoogleFormDefault.${key}`);
  };

  // Helper function to get nested text (for success/error messages)
  const getNestedFormText = (parent, child) => {

    // 1. Try to get from dynamicGoogleForm namespace in the same locale file
    if (formTextKey) {
      const dynamicText = t(`${formTextKey}.dynamicGoogleForm.${parent}.${child}`, { defaultValue: null });
      if (dynamicText !== null) return dynamicText;
    }

    // 2. Fall back to default form texts from dynamicGoogleFormDefault.json
    return t(`dynamicGoogleFormDefault.${parent}.${child}`);
  };

  // Merge in externally provided values whenever they change
  useEffect(() => {
    if (!values) return;

    setFieldValues((prev) => {
      const incomingEntries = Object.entries(values);
      const hasChanges = incomingEntries.some(
        ([key, value]) => prev[key] !== value
      );

      if (!hasChanges) {
        return prev;
      }

      return { ...prev, ...values };
    });
  }, [values]);

  const handleFieldChange = (fieldId, value) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldId]: value
    }));

    if (onFieldChange) {
      onFieldChange(fieldId, value);
    }
  };

  const formUrl = `https://script.google.com/macros/s/${formId}/exec`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");

    // Add breadcrumb for debugging
    addBreadcrumb("Form submission started", {
      formId,
      sheetName,
      fieldCount: Object.keys(fieldValues).length,
    });

    const formData = new FormData();
    formData.append("Language", i18n.language);
    formData.append("SheetName", sheetName);

    // Dynamically append all field values
    Object.entries(fieldValues).forEach(([key, value]) => {
      formData.append(key, value);
    });

    // Add promo source if provided
    if (promoSource) {
      formData.append("PromoSource", promoSource);
    }

    try {
      const response = await fetch(formUrl, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        // Get response text for better error context
        let responseText = "";
        try {
          responseText = await response.text();
        } catch (e) {
          responseText = "Unable to read response body";
        }

        // Create error object for Sentry
        const error = new Error(
          `Form submission failed: ${response.status} ${response.statusText}`
        );

        // Capture error with rich context in Sentry
        captureFormError(error, {
          formId,
          sheetName,
          responseStatus: response.status,
          responseText: responseText.substring(0, 500), // Limit to 500 chars
          formType: "google-apps-script",
          userId: orderId || undefined,
          fieldCount: Object.keys(fieldValues).length,
        });

        // Show existing toast to user
        showToastFn({
          show: true,
          title: getNestedFormText("error", "title"),
          message: getNestedFormText("error", "text"),
          autohide: false,
          variant: "danger",
        });
        return;
      }

      // Parse JSON response from Google Apps Script
      let jsonResponse;
      try {
        const responseText = await response.text();
        jsonResponse = JSON.parse(responseText);
      } catch (e) {
        // If parsing fails, treat as successful (backward compatibility)
        jsonResponse = { ok: true };
      }

      // Check if the Apps Script returned an error in the JSON response
      if (jsonResponse.ok === false) {
        addBreadcrumb("Form submission rejected by script", {
          formId,
          sheetName,
          error: jsonResponse.error,
          duplicate: jsonResponse.duplicate || false,
        });

        // Use internationalized error messages based on error type
        let errorTitle, errorMessage;
        if (jsonResponse.duplicate === true) {
          // Use duplicate-specific translated message
          errorTitle = getNestedFormText("duplicate", "title");
          errorMessage = getNestedFormText("duplicate", "text");
        } else {
          // Use generic error translated message
          errorTitle = getNestedFormText("error", "title");
          errorMessage = getNestedFormText("error", "text");
        }

        // Show error toast with the internationalized error message
        showToastFn({
          show: true,
          title: errorTitle,
          message: errorMessage,
          autohide: false,
          variant: "danger",
        });

        setErrorMessage(errorMessage);
        return;
      }

      addBreadcrumb("Form submission successful", {
        formId,
        sheetName,
      });

      showToastFn({
        show: true,
        title: getNestedFormText("success", "title"),
        message: getNestedFormText("success", "text"),
        autohide: true,
        variant: "success",
      });

      // If we have firstName, lastName, and phone in our field values, pass them to orderIdFn
      if (fieldValues.FirstName && fieldValues.LastName && fieldValues.PhoneNumber) {
        orderIdFn({
          firstName: fieldValues.FirstName,
          lastName: fieldValues.LastName,
          phone: fieldValues.PhoneNumber,
        });
      }

      setIsSuccess(true);
    } catch (error) {
      // Network error or other fetch failure
      // Capture network error in Sentry
      captureFormError(error, {
        formId,
        sheetName,
        formType: "google-apps-script",
        userId: orderId || undefined,
        fieldCount: Object.keys(fieldValues).length,
      });

      const errorMessage = getNestedFormText("error", "text");
      setErrorMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormContainer className={disabled ? "disabled" : ""}>
      {isLoading && (
        <LoadingOverlay>
          <Spinner animation="border" size="lg" />
          <p>{getFormText("submitting") || "Submitting..."}</p>
        </LoadingOverlay>
      )}
      {isSuccess && (
        <SuccessOverlay>
          <ResponsiveImage src="svg/Check.svg" alt="check-icon" />
          <h2>{getNestedFormText("success", "title")}</h2>
          <p>{getNestedFormText("success", "text")}</p>
        </SuccessOverlay>
      )}
      <StyledForm onSubmit={handleSubmit} className="form-container">
        {titles && (
          <SubTitleSection
            subTitle={getFormText("enterNow")}
            as={"h1"}
            text={getFormText("qualify")}
          />
        )}

        {formFields.map((field) => {
          // Check if this field needs a sign-up link (PlayerID + required)
          const needsSignUpLink = field.id === "PlayerID" && field.required;
          const isSelectField = field.type === "select" || Array.isArray(field.options);
          const placeholderText = getFormText(field.placeholder || field.label);
          const selectOptions = Array.isArray(field.options) ? field.options : [];

          return (
            <Form.Group key={field.id} controlId={`form${field.id}`}>
              {needsSignUpLink ? (
                <div className="label-link-container">
                  <FormLabel>{getFormText(field.label)}</FormLabel>
                  <Link to={t("routes.register")} className="form-link">
                    {getFormText("signUpNow")}
                  </Link>
                </div>
              ) : (
                <FormLabel>{getFormText(field.label)}</FormLabel>
              )}
              {isSelectField ? (
                <Form.Control
                  as="select"
                  name={field.id.toLowerCase()}
                  value={fieldValues[field.id] || ""}
                  onChange={(e) => handleFieldChange(field.id, e.target.value)}
                  required={field.required}
                  disabled={disabled}
                >
                  <option value="">{placeholderText}</option>
                  {selectOptions.map((option, index) => {
                    const optionValue = typeof option === "string" ? option : option?.value;
                    const optionLabel = typeof option === "string" ? option : option?.label || option?.value;
                    if (optionValue == null || optionValue === "") return null;

                    return (
                      <option key={`${field.id}-option-${index}`} value={optionValue}>
                        {optionLabel}
                      </option>
                    );
                  })}
                </Form.Control>
              ) : (
                <Form.Control
                  type={field.type || "text"}
                  name={field.id.toLowerCase()}
                  value={fieldValues[field.id] || ""}
                  onChange={(e) => handleFieldChange(field.id, e.target.value)}
                  placeholder={placeholderText}
                  required={field.required}
                  disabled={disabled}
                />
              )}
            </Form.Group>
          );
        })}

        {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

        <Button
          type="submit"
          disabled={isLoading || disabled}
          bgColor="var(--red)"
          color="var(--white)"
          hoverColor="var(--black)"
          hoverBgColor="var(--white)"
          fluid
          label={
            isLoading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              getFormText("submit")
            )
          }
        />
      </StyledForm>
    </FormContainer>
  );
};

const FormContainer = styled.div`
  position: relative;

  /* Disabled form input styles */
  .form-control:disabled {
    background-color: #2a2a2a !important;
    color: #888 !important;
    border-color: #444 !important;
    cursor: not-allowed !important;
    opacity: 0.6;
  }

  .form-label,
  label {
    transition: opacity 0.2s ease;
  }

  /* Dim labels when form container has disabled class */
  &.disabled .form-label,
  &.disabled label {
    opacity: 0.5;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 3rem;
  z-index: 3;

  p {
    margin-top: 1rem;
    font-size: 1.1rem;
  }
`;

const SuccessOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.95);
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 3rem 3rem 4rem;
  z-index: 3;
  img {
    max-width: 3rem;
    margin: 0.5rem;
  }
`;

export default DynamicGoogleFormSubmitter;
